/**
 * Connector geometry for the scene. Every connector is computed here from card
 * boxes and nothing else; its label point and knot point come from the same
 * points that build its path.
 *
 * Causal connectors (every type but relates_to / is_same) are orthogonal: out
 * of the source's right edge into the gutter after it, along a free horizontal
 * channel, down or up in the gutter before the target, into the target's left
 * edge. Gutters hold no cards, so only the horizontal runs can meet one; each
 * candidate is checked against every card and the first clear one is taken.
 * Associative connectors are freeform curves drawn behind the cards.
 */
import { orthPath } from '../components/orthogonalRoute';
import { cubicPoints, polylineHitsRect, simplify } from './geometry';

export const ASSOCIATIVE_TYPES = new Set(['relates_to', 'is_same']);
export const isCausalType = (type) => !ASSOCIATIVE_TYPES.has(type);

const left = (c) => c.x;
const right = (c) => c.x + c.w;

/** Index cards by column, for the "which cards could this run meet" question. */
export function createRouter(cards, config, colX) {
  const step = config.cardWidth + config.columnGap;
  const slotUse = new Map(); // gutter → links already routed through it

  /** Centre line of the gutter right after column c. */
  const gutterAfter = (c) => colX(c) + step / 2;

  /** A link's own vertical in a gutter, fanned out around the centre (the tree's line). */
  const slotIn = (c) => {
    const n = slotUse.get(c) || 0;
    slotUse.set(c, n + 1);
    const k = Math.floor(n / 2) + 1;
    const max = Math.floor((config.columnGap / 2 - config.cornerRadius - 8) / config.linkSlot);
    const kk = ((k - 1) % Math.max(1, max)) + 1;
    return gutterAfter(c) + (n % 2 ? 1 : -1) * kk * config.linkSlot;
  };

  const clear = (points, allowed) => !cards.some((card) => !allowed.has(card.id) && polylineHitsRect(points, card));

  // Horizontal channels already taken, so two routes never share a run they
  // do not share a target for.
  const taken = [];
  const reserve = (y, xa, xb) => taken.push({ y, lo: Math.min(xa, xb), hi: Math.max(xa, xb) });
  const isTaken = (y, lo, hi) => taken.some((r) => Math.abs(r.y - y) < 6 && r.lo < hi - 1 && lo < r.hi - 1);

  /** A horizontal y between xa and xb meeting no card and no taken channel, nearest to `want`. */
  const freeChannel = (xa, xb, want, allowed) => {
    const lo = Math.min(xa, xb);
    const hi = Math.max(xa, xb);
    const inRange = cards.filter((c) => !allowed.has(c.id) && right(c) > lo && left(c) < hi);
    const half = config.rowGap / 2;
    const offsets = [0, -10, 10, -60, 60, -110, 110, -160, 160];
    const candidates = new Set();
    inRange.forEach((c) => offsets.forEach((d) => { candidates.add(c.y - half + d); candidates.add(c.y + c.h + half + d); }));
    if (!inRange.length) candidates.add(want);
    let best = null;
    candidates.forEach((y) => {
      if (inRange.some((c) => y > c.y - 6 && y < c.y + c.h + 6) || isTaken(y, lo, hi)) return;
      const d = Math.abs(y - want);
      if (best === null || d < best.d - 1e-6 || (Math.abs(d - best.d) < 1e-6 && y < best.y)) best = { y, d };
    });
    return best ? best.y : want;
  };

  /**
   * One vertical per source and type for its links: the links of one type leave
   * together and split where they must, so the type is said once where they run
   * together (sceneLabels.js).
   */
  const ports = new Map();
  const portOf = (s, type) => {
    const key = `${s.id}|${type || ''}`;
    if (!ports.has(key)) ports.set(key, slotIn(s.column));
    return ports.get(key);
  };

  /**
   * Several connectors into one target as one trunk: every source drops into
   * a shared channel in its own gutter, as early as the leftmost source, and
   * the trunk carries them into the target. The target's own row when it is
   * free (the tree's line is already on it), else a free channel and a turn in
   * the gutter before the target. Returns each member's full route, the trunk
   * and each member's stub (the part only it draws).
   */
  const bundle = (targets, members) => {
    // Only the targets may touch the trunk: a source sitting on the trunk's
    // line would have every other member's route run through it.
    const allowed = new Set(targets.map((t) => t.id));
    const col = targets[0].column;
    const ends = new Map(targets.map((t) => [t.id, { x: left(t), y: t.cy }]));
    // Each source leaves by its own port (the tree's line for a tree edge), so
    // its links to several targets leave together and split late (fan-out).
    const portX = members.map((m) => (m.tree || m.treeLine ? gutterAfter(m.s.column) : portOf(m.s, m.type)));
    const xmin = Math.min(...portX);
    // The trunk is drawn as whole polylines, card to card, so every corner is
    // rounded with the run before and after it: it begins at the leftmost source
    // and carries that source's drop itself; every other piece starts or ends on
    // a straight run of another (a T), never at another piece's end.
    // The trunk begins at the leftmost accepted source (a proposal's stub is dashed, and
    // a trunk drawn solid under it would show through); a proposal further left runs
    // along the trunk's line to where the trunk begins, joining it end to end.
    const pool = members.map((m, i) => i).filter((i) => !members[i].proposed);
    const first = (pool.length ? pool : members.map((m, i) => i)).reduce((b, i) => (portX[i] < portX[b] ? i : b));
    const x0 = portX[first];
    const leadIn = [{ x: right(members[first].s), y: members[first].s.cy }, { x: x0, y: members[first].s.cy }];
    const single = targets.length === 1 ? targets[0] : null;
    const rowFree = single && clear([{ x: xmin, y: single.cy }, ends.get(single.id)], allowed) && !isTaken(single.cy, xmin, left(single));
    if (rowFree) {
      // The target's own row is free all the way: the trunk is that row.
      const end = ends.get(single.id);
      reserve(end.y, xmin, end.x);
      const trunk = simplify([...leadIn, { x: x0, y: end.y }, end]);
      const routes = members.map((m, i) => {
        // A stub lands on a straight run of the trunk, past the rounded corner at its start.
        const stub = i === first ? leadIn : simplify([{ x: right(m.s), y: m.s.cy }, { x: portX[i], y: m.s.cy }, { x: portX[i], y: end.y }, { x: Math.max(portX[i], x0 + config.cornerRadius), y: end.y }]);
        const points = i === first ? trunk : simplify([...stub, end]);
        return { points, stub, clear: clear(points, new Set([m.s.id, single.id])) };
      });
      return { lines: [trunk], routes, via: 'trunk-row' };
    }
    // A channel to the gutter before the target column, a spine down it, and a
    // branch into each target. The spine stands in that gutter, never past the
    // targets' left edge.
    const lastGutter = gutterAfter(col - 1);
    const before = colX(col) - config.cardWidth / 2;
    const g2 = Math.max(slotIn(col - 1), ...portX.filter((x) => x > lastGutter - config.columnGap / 2 && x < before));
    const want = targets.reduce((s, t) => s + t.cy, 0) / targets.length;
    const y = freeChannel(xmin, g2, want, allowed);
    reserve(y, xmin, g2);
    const tys = targets.map((t) => t.cy);
    const lo = Math.min(y, ...tys);
    const hi = Math.max(y, ...tys);
    const at = (cy) => targets.find((t) => Math.abs(t.cy - cy) < 0.01);
    const channel = [...leadIn, { x: x0, y }, { x: g2, y }];
    const branch = (t) => [{ x: g2, y: t.cy }, ends.get(t.id)];
    const onRow = at(y);
    let lines;
    if (y <= Math.min(...tys) + 0.01 && !onRow) {
      const far = at(hi);
      lines = [simplify([...channel, { x: g2, y: hi }, ends.get(far.id)]), ...targets.filter((t) => t !== far).map(branch)];
    } else if (y >= Math.max(...tys) - 0.01 && !onRow) {
      const far = at(lo);
      lines = [simplify([...channel, { x: g2, y: lo }, ends.get(far.id)]), ...targets.filter((t) => t !== far).map(branch)];
    } else if (onRow) {
      // The channel runs straight into the target on its row; the spine leaves it up and down.
      const above = targets.filter((t) => t.cy < y - 0.01);
      const below = targets.filter((t) => t.cy > y + 0.01);
      lines = [simplify([...channel, ends.get(onRow.id)])];
      if (above.length) lines.push(simplify([{ x: g2, y }, { x: g2, y: lo }, ends.get(at(lo).id)]));
      if (below.length) lines.push(simplify([{ x: g2, y }, { x: g2, y: hi }, ends.get(at(hi).id)]));
      targets.filter((t) => t !== onRow && t.cy !== lo && t.cy !== hi).forEach((t) => lines.push(branch(t)));
    } else {
      // The channel meets the spine between two targets: a T onto a spine that is one line, card to card.
      lines = [simplify(channel), simplify([ends.get(at(lo).id), { x: g2, y: lo }, { x: g2, y: hi }, ends.get(at(hi).id)]), ...targets.filter((t) => t.cy !== lo && t.cy !== hi).map(branch)];
    }
    const routes = members.map((m, i) => {
      const gx = portX[i];
      const end = ends.get(m.t.id);
      const start = { x: right(m.s), y: m.s.cy };
      // A source whose port is the spine joins it where its row meets it,
      // rather than climbing to the channel and coming back down.
      const onSpine = Math.abs(gx - g2) < 0.01;
      // Joins land on straight runs: inside the spine's corners, past the channel's first corner.
      const r = config.cornerRadius;
      const jy = onSpine ? (hi - lo > 2 * r ? Math.min(Math.max(m.s.cy, lo + r), hi - r) : (lo + hi) / 2) : y;
      const stub = i === first ? leadIn : simplify([start, { x: gx, y: m.s.cy }, { x: gx, y: jy }, { x: onSpine ? gx : Math.min(Math.max(gx, x0 + r), g2), y: jy }]);
      const points = simplify(i === first ? [...leadIn, { x: x0, y }, { x: g2, y }, { x: g2, y: end.y }, end] : [...stub, { x: g2, y: jy }, { x: g2, y: end.y }, end]);
      return { points, stub, clear: clear(points, new Set([m.s.id, m.t.id])) };
    });
    return { lines, routes, via: 'trunk-channel', channelY: y, spineX: g2 };
  };

  /**
   * Orthogonal route from card s to card t (t's left edge). `tree` uses the
   * gutter's centre line (siblings share it, as an org chart); links get a slot.
   */
  const orthogonal = (s, t, { tree = false, treeLine = false, preferBeforeTarget = false, type = null } = {}) => {
    const allowed = new Set([s.id, t.id]);
    const start = { x: right(s), y: s.cy };
    const end = { x: left(t), y: t.cy };
    if (Math.abs(start.y - end.y) < 0.5 && end.x > start.x) {
      const straight = [start, end];
      if (clear(straight, allowed)) return { points: straight, via: 'straight' };
    }
    const g1 = tree || treeLine ? gutterAfter(s.column) : portOf(s, type);
    const forward = t.column > s.column;
    const g2 = forward ? (tree ? gutterAfter(t.column - 1) : slotIn(t.column - 1)) : slotIn(t.column - 1);
    const candidates = [];
    const turnAfter = [start, { x: g1, y: start.y }, { x: g1, y: end.y }, end];
    const turnBefore = [start, { x: g2, y: start.y }, { x: g2, y: end.y }, end];
    if (forward) {
      if (preferBeforeTarget) candidates.push(turnBefore, turnAfter);
      else candidates.push(turnAfter, turnBefore);
    }
    for (const pts of candidates) {
      const p = simplify(pts);
      if (clear(p, allowed)) return { points: p, via: 'turn' };
    }
    const y = freeChannel(g1, g2, (start.y + end.y) / 2, allowed);
    reserve(y, g1, g2);
    const channel = simplify([start, { x: g1, y: start.y }, { x: g1, y }, { x: g2, y }, { x: g2, y: end.y }, end]);
    return { points: channel, via: 'channel', clear: clear(channel, allowed) };
  };

  return { orthogonal, bundle, gutterAfter };
}

/**
 * Label and knot on the last run of an orthogonal route: the knot a fixed
 * distance before the end, the label before the knot, clear of the corner it
 * comes out of. Both lie on the run itself.
 */
export function labelAndKnot(points, config) {
  const end = points[points.length - 1];
  const prev = points[points.length - 2];
  const dir = Math.sign(end.x - prev.x) || 1;
  const runLen = Math.abs(end.x - prev.x) + Math.abs(end.y - prev.y);
  const horizontal = Math.abs(end.y - prev.y) < 0.01;
  const along = (d) => (horizontal
    ? { x: end.x - dir * d, y: end.y }
    : { x: end.x, y: end.y - Math.sign(end.y - prev.y) * d });
  const corner = points.length > 2 ? config.cornerRadius : 0;
  const knotD = Math.min(config.knotInset, runLen / 2);
  const knot = along(knotD);
  const room = runLen - corner; // usable distance from the end back to the corner
  const wantD = knotD + config.knotRadius + config.labelGap + config.labelHalfWidth;
  const labelD = room >= wantD + config.labelHalfWidth ? wantD : Math.max(knotD, (room + knotD) / 2);
  return { label: along(Math.min(labelD, runLen)), knot };
}

/** Freeform curve behind the cards (associative links): sampled, so label and knot are on it. */
export function curveBetween(s, t, config) {
  const forward = t.cx >= s.cx;
  const a = { x: forward ? right(s) : left(s), y: s.cy };
  const b = { x: forward ? left(t) : right(t), y: t.cy };
  const pull = Math.max(80, Math.abs(b.x - a.x) * 0.4) * (forward ? 1 : -1);
  const c1 = { x: a.x + pull, y: a.y };
  const c2 = { x: b.x - pull, y: b.y };
  const n = config.curveSamples;
  const points = cubicPoints(a, c1, c2, b, n);
  const d = `M ${a.x} ${a.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${b.x} ${b.y}`;
  return { points, path: d, label: points[Math.round(n / 2)], knot: points[Math.round(n * 0.8)] };
}

export function orthD(points, config) {
  return orthPath(points, { radius: config.cornerRadius });
}
